const React = require("react");
const { useSyncee } = require("../context/SynceeContext");
const { useResource } = require("../hooks/useResource");
const { PageHeader, Button, ResourceState, Kpi, StatusBadge } = require("../components/ui");
const h = React.createElement;

const DropshippingAdmin = () => {
  const { api, t, isAdmin } = useSyncee();
  const resource = useResource(async () => ({
    ...(await api.getPlatform()),
    ...(await api.getAdminOverview()),
    ...(await api.getAdminConnections()),
  }), [api]);
  const [busy, setBusy] = React.useState(false);
  const [message, setMessage] = React.useState("");
  const [error, setError] = React.useState("");
  const [config, setConfig] = React.useState({ apiBaseUrl: "", websiteUrl: "", apiDocUrl: "", authType: "token", returnPolicy: "" });
  if (!isAdmin) return h("div", { className: "synds-state" }, t("h_syncee_admin_only", "Administration access required."));
  const overview = resource.data?.overview || {};
  const connections = resource.data?.connections || [];
  const platform = resource.data?.platform;
  React.useEffect(() => {
    if (!platform) return;
    setConfig({
      apiBaseUrl: platform.apiBaseUrl || "",
      websiteUrl: platform.websiteUrl || "",
      apiDocUrl: platform.apiDocUrl || "",
      authType: platform.authType || "token",
      returnPolicy: platform.returnPolicy || "",
    });
  }, [platform?._id, platform?.updatedAt]);
  const action = async (callback, success) => {
    setBusy(true); setMessage(""); setError("");
    try { await callback(); setMessage(success); await resource.reload(); }
    catch (requestError) { setError(requestError.message); }
    finally { setBusy(false); }
  };
  return h(React.Fragment, null,
    h(PageHeader, {
      title: t("h_syncee_admin_title", "Syncee administration"),
      action: h("div", { className: "synds-row" },
        h(Button, { disabled: busy, variant: platform?.status === "active" ? "danger" : "", onClick: () => action(() => api.updateAdminPlatform({ status: platform?.status === "active" ? "inactive" : "active" }), t("h_syncee_platform_updated", "Syncee platform status updated.")) }, platform?.status === "active" ? t("h_syncee_disable", "Disable Syncee") : t("h_syncee_enable", "Enable Syncee")),
        h(Button, { disabled: busy, variant: "secondary", onClick: () => action(api.syncAllSellers, t("h_syncee_admin_sync_done", "All active Syncee sellers were synchronized.")) }, t("h_syncee_sync_all_sellers", "Sync all sellers"))
      ),
    }),
    message ? h("div", { className: "synds-success" }, message) : null,
    error ? h("div", { className: "synds-alert" }, error) : null,
    h(ResourceState, { loading: resource.loading, error: resource.error, empty: false, reload: resource.reload, t },
      h(React.Fragment, null,
        h("form", { className: "synds-form", onSubmit: (event) => { event.preventDefault(); action(() => api.updateAdminPlatform(config), t("h_syncee_config_saved", "Syncee configuration saved.")); } },
          h("h3", null, t("h_syncee_config", "Platform configuration")),
          h("div", { className: "synds-field" }, h("label", { htmlFor: "syncee-admin-api" }, t("h_syncee_api_url", "Partner API URL")), h("input", { id: "syncee-admin-api", className: "synds-input", type: "url", required: true, value: config.apiBaseUrl, onChange: (event) => setConfig((old) => ({ ...old, apiBaseUrl: event.target.value })) })),
          h("div", { className: "synds-field" }, h("label", { htmlFor: "syncee-admin-site" }, t("h_syncee_website", "Official website")), h("input", { id: "syncee-admin-site", className: "synds-input", type: "url", value: config.websiteUrl, onChange: (event) => setConfig((old) => ({ ...old, websiteUrl: event.target.value })) })),
          h("div", { className: "synds-field" }, h("label", { htmlFor: "syncee-admin-docs" }, t("h_syncee_docs", "Documentation URL")), h("input", { id: "syncee-admin-docs", className: "synds-input", type: "url", value: config.apiDocUrl, onChange: (event) => setConfig((old) => ({ ...old, apiDocUrl: event.target.value })) })),
          h("div", { className: "synds-field" }, h("label", { htmlFor: "syncee-admin-auth" }, t("h_syncee_auth_type", "Authentication type")), h("select", { id: "syncee-admin-auth", className: "synds-input", value: config.authType, onChange: (event) => setConfig((old) => ({ ...old, authType: event.target.value })) }, h("option", { value: "token" }, t("h_syncee_auth_token", "Token")), h("option", { value: "api_key" }, t("h_syncee_auth_api_key", "API key")), h("option", { value: "oauth2" }, t("h_syncee_auth_oauth2", "OAuth 2")))),
          h("div", { className: "synds-field" }, h("label", { htmlFor: "syncee-admin-return" }, t("h_syncee_return_policy", "Return policy")), h("textarea", { id: "syncee-admin-return", className: "synds-input", rows: 4, value: config.returnPolicy, onChange: (event) => setConfig((old) => ({ ...old, returnPolicy: event.target.value })) })),
          h(Button, { type: "submit", disabled: busy }, t("h_syncee_save", "Save configuration"))
        ),
        h("div", { className: "synds-kpis" },
          h(Kpi, { value: overview.connections || 0, label: t("h_syncee_connections", "Connections") }),
          h(Kpi, { value: overview.products || 0, label: t("h_syncee_products", "Products") }),
          h(Kpi, { value: overview.orders || 0, label: t("h_syncee_orders", "Orders") }),
          h(Kpi, { value: overview.errors || 0, label: t("h_syncee_errors", "Errors") })
        ),
        h("div", { className: "synds-table-wrap" }, h("table", { className: "synds-table" },
          h("thead", null, h("tr", null, h("th", null, t("h_syncee_seller", "Seller")), h("th", null, t("h_syncee_store", "Store")), h("th", null, t("h_syncee_status", "Status")))),
          h("tbody", null, ...connections.map((connection) => h("tr", { key: connection._id },
            h("td", null, connection.sellerId?.email || connection.sellerId?._id),
            h("td", null, connection.storeName || connection.externalStoreId || "—"),
            h("td", null, h(StatusBadge, { status: connection.status, t }))
          )))
        ))
      )
    )
  );
};

module.exports = { DropshippingAdmin };
